import {KpiScore} from '@hconnect/uikit/src/lib2'
import {InfoOutlined} from '@mui/icons-material'
import {Box, Stack, Tooltip, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'

import {DOWNTIME_ANALYTICS_WIDGET_NAMESPACE} from '../consts'

type Props = {
  longKilnStoppageRcfaCount: number
  lateLongKilnStoppageRcfaCount: number
  overdueKilnStoppageRcfaCount: number
  totalOverdueKilnStoppageRcfaCount: number
  stoppageRcfaCount: number
  timeRangeLabel: string
}

export const RcfaSummaryExtended = ({
  longKilnStoppageRcfaCount,
  overdueKilnStoppageRcfaCount,
  totalOverdueKilnStoppageRcfaCount,
  lateLongKilnStoppageRcfaCount,
  stoppageRcfaCount,
  timeRangeLabel
}: Props) => {
  const {t} = useTranslation(DOWNTIME_ANALYTICS_WIDGET_NAMESPACE)
  const isOverdueWarningShown =
    totalOverdueKilnStoppageRcfaCount > 0 &&
    overdueKilnStoppageRcfaCount !== totalOverdueKilnStoppageRcfaCount
  const overdueVariant = totalOverdueKilnStoppageRcfaCount > 0 ? 'negative' : 'neutral'
  const optionalRcfaCount = stoppageRcfaCount - longKilnStoppageRcfaCount

  return (
    <Stack
      direction="row"
      sx={{
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        mt: 2
      }}
      spacing={3}
    >
      <StackItem>
        <Tooltip arrow placement="right" title={t('rcfaSummary.label.lateTooltipTitle')}>
          <span>
            <KpiScore
              labelIcon={<InfoOutlined />}
              label={t('rcfaSummary.label.late')}
              value={lateLongKilnStoppageRcfaCount}
              data-test-id="rcfa-summary-extended-late"
            />
          </span>
        </Tooltip>
      </StackItem>
      <StackItem>
        <KpiScore
          label={t('rcfaSummary.label.optional')}
          value={optionalRcfaCount}
          data-test-id="rcfa-summary-extended-mandatory"
        />
      </StackItem>
      <StackItem>
        <Tooltip
          title={
            isOverdueWarningShown ? (
              <OverdueLabelTooltip
                overdue={overdueKilnStoppageRcfaCount}
                totalOverdue={totalOverdueKilnStoppageRcfaCount}
                timeRangeLabel={timeRangeLabel}
              />
            ) : null
          }
          arrow
          placement="right"
        >
          <span>
            <KpiScore
              label={t('rcfaSummary.label.overdue')}
              labelIcon={isOverdueWarningShown ? <InfoOutlined /> : undefined}
              value={totalOverdueKilnStoppageRcfaCount}
              variant={overdueVariant}
              data-test-id="rcfa-summary-extended-overdue"
            />
          </span>
        </Tooltip>
      </StackItem>
    </Stack>
  )
}

type StackItemProps = {
  children: React.ReactNode
  sx?: object
}

const StackItem: React.FC<StackItemProps> = ({children, sx}) => (
  <Box
    sx={{
      display: 'flex',
      ...sx
    }}
  >
    {children}
  </Box>
)

const OverdueLabelTooltip = ({
  overdue,
  totalOverdue,
  timeRangeLabel
}: {
  overdue: number
  totalOverdue: number
  timeRangeLabel: string
}) => {
  const {t} = useTranslation(DOWNTIME_ANALYTICS_WIDGET_NAMESPACE)

  return (
    <Stack spacing={0.5}>
      <Typography fontWeight="medium">
        {`${totalOverdue} ${t('rcfaSummary.label.overdue')} (${t('rcfaSummary.label.allTime')})`}
      </Typography>
      <Typography>{`${overdue} ${t('rcfaSummary.label.overdue')} (${timeRangeLabel})`}</Typography>
    </Stack>
  )
}
